import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

export const getSuppliers = createAsyncThunk(
  "supplier/getSuppliers",
  async ({ status = "ACTIVE", search = "", page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (status) params.status = status;
      if (search) params.search = search;
      if (page) params.page = page;
      if (limit) params.limit = limit;

      const response = await axios.get("/api/business/suppliers", { params });
      return response.response || response.data || response;
    } catch (err) {
      return rejectWithValue(err || "Failed to fetch suppliers");
    }
  }
);

export const getSupplierById = createAsyncThunk(
  "supplier/getSupplierById",
  async (supplierId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/business/suppliers/${supplierId}`);
      return response.response || response.data || response;
    } catch (err) {
      return rejectWithValue(err || "Failed to fetch supplier details");
    }
  }
);

export const createSupplier = createAsyncThunk(
  "supplier/createSupplier",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/business/suppliers", payload);
      return response;
    } catch (err) {
      return rejectWithValue(err || "Failed to create supplier");
    }
  }
);

export const updateSupplier = createAsyncThunk(
  "supplier/updateSupplier",
  async ({ supplierId, payload }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/api/business/suppliers/${supplierId}`, payload);
      return response;
    } catch (err) {
      return rejectWithValue(err || "Failed to update supplier");
    }
  }
);

export const updateSupplierStatus = createAsyncThunk(
  "supplier/updateSupplierStatus",
  async ({ supplierId, status, remarks, reason }, { rejectWithValue }) => {
    try {
      const remarksVal = remarks !== undefined ? remarks : (reason !== undefined ? reason : "");
      const response = await axios.patch(`/api/business/suppliers/${supplierId}/status`, {
        status,
        remarks: remarksVal,
        reason: remarksVal,
      });
      return response.response || response.data || response;
    } catch (err) {
      return rejectWithValue(err || "Failed to update supplier status");
    }
  }
);

export const getSupplierActivityLogs = createAsyncThunk(
  "supplier/getSupplierActivityLogs",
  async ({ supplierId, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/business/suppliers/${supplierId}/activity-logs`, {
        params: { page, limit },
      });
      return response.response || response.data || response;
    } catch (err) {
      return rejectWithValue(err || "Failed to fetch supplier activity logs");
    }
  }
);

export const verifyIfsc = createAsyncThunk(
  "supplier/verifyIfsc",
  async (payload, { rejectWithValue }) => {
    try {
      const ifscCode =
        typeof payload === "string"
          ? payload
          : payload?.ifscCode || payload?.ifsc || "";
      const response = await axios.post("/api/business/verifyIfsc", {
        ifscCode,
        ifsc: ifscCode,
      });
      return (
        response?.response ||
        response?.data?.response ||
        response?.data ||
        response
      );
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          err ||
          "Failed to verify IFSC"
      );
    }
  }
);

export const verifySupplierPan = createAsyncThunk(
  "supplier/verifySupplierPan",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/business/suppliers/verify-pan",
        payload
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "Failed to verify PAN");
    }
  }
);

export const verifySupplierGstin = createAsyncThunk(
  "supplier/verifySupplierGstin",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/business/suppliers/verify-gstin",
        payload
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "Failed to verify GSTIN");
    }
  }
);

export const verifySupplierBank = createAsyncThunk(
  "supplier/verifySupplierBank",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/business/suppliers/verify-bank",
        payload
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "Failed to verify Bank Account");
    }
  }
);

export const supplierSlice = createSlice({
  name: "supplier",
  initialState: {
    loading: false,
    detailsLoading: false,
    submitLoading: false,
    statusLoading: false,
    ifscLoading: false,
    ifscData: null,
    suppliersList: [],
    supplierDetailedData: null,
    activityLogs: [],
    logsTotalRecords: 0,
    logsLoading: false,
    pagination: {
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
      limit: 10,
    },
    counts: {
      ALL: 0,
      ACTIVE: 0,
      INACTIVE: 0,
    },
    error: null,
    submitError: null,
  },
  reducers: {
    clearSupplierErrors: (state) => {
      state.error = null;
      state.submitError = null;
    },
    clearIfscData: (state) => {
      state.ifscData = null;
    },
    setSupplierCounts: (state, action) => {
      state.counts = { ...state.counts, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // getSuppliers
    builder.addCase(getSuppliers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getSuppliers.fulfilled, (state, action) => {
      state.loading = false;
      const data = action.payload || {};
      state.suppliersList = data.suppliers || [];
      state.pagination = {
        totalItems: data.totalItems || 0,
        totalPages: data.totalPages || 1,
        currentPage: data.currentPage || 1,
        limit: 10,
      };
    });
    builder.addCase(getSuppliers.rejected, (state, action) => {
      state.loading = false;
      state.suppliersList = [];
      state.error = action.payload || "Failed to fetch suppliers";
    });

    // getSupplierById
    builder.addCase(getSupplierById.pending, (state) => {
      state.detailsLoading = true;
      state.error = null;
    });
    builder.addCase(getSupplierById.fulfilled, (state, action) => {
      state.detailsLoading = false;
      state.supplierDetailedData = action.payload;
    });
    builder.addCase(getSupplierById.rejected, (state, action) => {
      state.detailsLoading = false;
      state.supplierDetailedData = null;
      state.error = action.payload || "Failed to fetch supplier details";
    });

    // createSupplier
    builder.addCase(createSupplier.pending, (state) => {
      state.submitLoading = true;
      state.submitError = null;
    });
    builder.addCase(createSupplier.fulfilled, (state) => {
      state.submitLoading = false;
      state.submitError = null;
    });
    builder.addCase(createSupplier.rejected, (state, action) => {
      state.submitLoading = false;
      state.submitError = action.payload || "Failed to create supplier";
    });

    // updateSupplier
    builder.addCase(updateSupplier.pending, (state) => {
      state.submitLoading = true;
      state.submitError = null;
    });
    builder.addCase(updateSupplier.fulfilled, (state) => {
      state.submitLoading = false;
      state.submitError = null;
    });
    builder.addCase(updateSupplier.rejected, (state, action) => {
      state.submitLoading = false;
      state.submitError = action.payload || "Failed to update supplier";
    });

    // updateSupplierStatus
    builder.addCase(updateSupplierStatus.pending, (state) => {
      state.statusLoading = true;
      state.error = null;
    });
    builder.addCase(updateSupplierStatus.fulfilled, (state, action) => {
      state.statusLoading = false;
      const { supplierId, status, remarks, reason } = action.meta?.arg || {};
      const finalRemarks = remarks !== undefined ? remarks : (reason !== undefined ? reason : "");
      if (supplierId) {
        const item = state.suppliersList.find((s) => s.supplierId === supplierId);
        if (item) {
          item.status = status;
          item.remarks = finalRemarks;
          item.reason = finalRemarks;
          item.isActive = status === "ACTIVE";
        }
        if (
          state.supplierDetailedData &&
          state.supplierDetailedData.supplierId === supplierId
        ) {
          state.supplierDetailedData.status = status;
          state.supplierDetailedData.remarks = finalRemarks;
          state.supplierDetailedData.reason = finalRemarks;
          state.supplierDetailedData.isActive = status === "ACTIVE";
        }
      }
    });
    builder.addCase(updateSupplierStatus.rejected, (state, action) => {
      state.statusLoading = false;
      state.error = action.payload || "Failed to update supplier status";
    });

    // getSupplierActivityLogs
    builder.addCase(getSupplierActivityLogs.pending, (state) => {
      state.logsLoading = true;
    });
    builder.addCase(getSupplierActivityLogs.fulfilled, (state, action) => {
      state.logsLoading = false;
      const data = action.payload || {};
      state.activityLogs = data.logs || [];
      state.logsTotalRecords = data.totalRecords || 0;
    });
    builder.addCase(getSupplierActivityLogs.rejected, (state) => {
      state.logsLoading = false;
      state.activityLogs = [];
      state.logsTotalRecords = 0;
    });

    // verifyIfsc
    builder.addCase(verifyIfsc.pending, (state) => {
      state.ifscLoading = true;
    });
    builder.addCase(verifyIfsc.fulfilled, (state, action) => {
      state.ifscLoading = false;
      state.ifscData = action.payload;
    });
    builder.addCase(verifyIfsc.rejected, (state) => {
      state.ifscLoading = false;
      state.ifscData = null;
    });
  },
});

export const { clearSupplierErrors, clearIfscData, setSupplierCounts } = supplierSlice.actions;

export default supplierSlice.reducer;
